import z from 'zod';

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type CustomSchemaCollection<TSchemas extends Record<string, z.AnyZodObject>> = {
  schemas: TSchemas;
  subjectMapping: Record<keyof TSchemas, { subjectName: string; action: string }>;
  addSchemas: <TSubjectName extends string, TActions extends Record<string, z.AnyZodObject>>(
    subjectName: TSubjectName,
    actions: TActions,
  ) => CustomSchemaCollection<
    TSchemas & {
      [P in `${TSubjectName}${Capitalize<string & keyof TActions>}`]: P extends `${TSubjectName}${infer A}`
        ? TActions[Uncapitalize<A>]
        : never;
    }
  >;
};

const createSchemaCollection = <TSchemas extends Record<string, z.AnyZodObject>>(
  schemas: TSchemas,
  subjectMapping: Record<keyof TSchemas, { subjectName: string; action: string }>,
): CustomSchemaCollection<TSchemas> => ({
  schemas,
  subjectMapping,
  addSchemas: <TSubjectName extends string, TActions extends Record<string, z.AnyZodObject>>(
    subjectName: TSubjectName,
    actions: TActions,
  ) =>
    createSchemaCollection<
      TSchemas & {
        [P in `${TSubjectName}${Capitalize<string & keyof TActions>}`]: P extends `${TSubjectName}${infer A}`
          ? TActions[Uncapitalize<A>]
          : never;
      }
    >(
      Object.entries(actions).reduce(
        (total, [action, schema]) => {
          total[`${subjectName}${capitalize(action)}`] = schema as any;
          return total;
        },
        { ...schemas } as TSchemas & {
          [P in `${TSubjectName}${Capitalize<string & keyof TActions>}`]: P extends `${TSubjectName}${infer A}`
            ? TActions[Uncapitalize<A>]
            : never;
        },
      ),
      Object.keys(actions).reduce(
        (total, action) => {
          total[`${subjectName}${capitalize(action)}`] = { subjectName, action };
          return total;
        },
        { ...subjectMapping } as Record<
          keyof TSchemas | `${TSubjectName}${Capitalize<string & keyof TActions>}`,
          { subjectName: string; action: string }
        >,
      ),
    ),
});

export default createSchemaCollection({}, {});
